// ============================================================================
// THE SHAPE OF AN ANSWER — the small markup the chat model is allowed, parsed.
//
// Owner, 2026-09-20, looking at a week's to-do list rendered as one 900-word paragraph: "the
// formatting is horrible … currently this is just a big chunk of text." Two things made it so:
// the Worker told the model to write plain prose, and this app rendered whatever came back inside
// one <p>, line breaks and all flattened. Both changed the same day.
//
// This is NOT a Markdown renderer. The model is allowed exactly four things (lib/chat.ts on the
// Worker, `FORMAT_RULES`): paragraphs, `- ` bullets, `1. ` numbered items, and **bold** — plus a
// short bold label on a line of its own to head a group. Anything else it writes is shown as the
// text it typed. A heading it was told not to write loses its `#` and becomes a label rather than
// a line of literal hashes, because the reader should never pay for the model's slip. No HTML is
// ever produced from the text: the output is data, and the components below build the elements.
//
// The parse is also what the answer's arrival is timed against (lib/motion.ts, 13e): a list item
// is one clause, a paragraph is its sentences. `answerClauses` is the single definition of that.
// ============================================================================

import { splitClauses } from './motion'

/** One run of inline text; `bold` is the only inline emphasis the model may use. */
export interface Run {
  text: string
  bold: boolean
}

export type Block =
  | { kind: 'paragraph'; runs: Run[]; /** A bold line on its own, heading the lines after it. */ label: boolean }
  | { kind: 'list'; ordered: boolean; items: Run[][] }

const BULLET = /^\s*(?:[-*•]|\d{1,2}[.)])\s+/
const ORDERED = /^\s*\d{1,2}[.)]\s+/
const HEADING = /^\s*#{1,6}\s+/

/** `**bold**` runs, in order; a lone or unclosed `**` is text. */
export function inlineRuns(line: string): Run[] {
  const runs: Run[] = []
  const re = /\*\*([^*\n]+?)\*\*/g
  let last = 0
  for (let m = re.exec(line); m !== null; m = re.exec(line)) {
    if (m.index > last) runs.push({ text: line.slice(last, m.index), bold: false })
    runs.push({ text: m[1]!, bold: true })
    last = m.index + m[0].length
  }
  if (last < line.length) runs.push({ text: line.slice(last), bold: false })
  return runs.length > 0 ? runs : [{ text: '', bold: false }]
}

/** A line that is nothing but one bold run, optionally ending in a colon: a group's label. */
function isLabel(runs: Run[]): boolean {
  if (runs.length === 1) return runs[0]!.bold
  if (runs.length === 2) return runs[0]!.bold && runs[1]!.text.trim() === ':' && !runs[1]!.bold
  return false
}

export function parseAnswer(text: string): Block[] {
  const blocks: Block[] = []
  let para: string[] = []
  const flushPara = () => {
    if (para.length === 0) return
    const joined = para.join(' ').replace(/\s+/g, ' ').trim()
    para = []
    if (!joined) return
    const runs = inlineRuns(joined)
    blocks.push({ kind: 'paragraph', runs, label: isLabel(runs) })
  }

  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trimEnd()
    if (line.trim() === '') {
      flushPara()
      continue
    }
    if (BULLET.test(line)) {
      flushPara()
      const ordered = ORDERED.test(line)
      const item = inlineRuns(line.replace(BULLET, '').trim())
      const last = blocks[blocks.length - 1]
      if (last && last.kind === 'list' && last.ordered === ordered) last.items.push(item)
      else blocks.push({ kind: 'list', ordered, items: [item] })
      continue
    }
    if (HEADING.test(line)) {
      // Told not to; shown as a label rather than as hashes.
      flushPara()
      const runs = inlineRuns(line.replace(HEADING, '').trim())
      blocks.push({
        kind: 'paragraph',
        runs: [{ text: runs.map((r) => r.text).join(''), bold: true }],
        label: true,
      })
      continue
    }
    // A bold-only line is a label even with no blank line around it — the model writes
    // "**This week**\n- item" as often as not.
    const runs = inlineRuns(line.trim())
    if (isLabel(runs)) {
      flushPara()
      blocks.push({ kind: 'paragraph', runs, label: true })
      continue
    }
    para.push(line.trim())
  }
  flushPara()
  return blocks.length > 0 ? blocks : [{ kind: 'paragraph', runs: [{ text, bold: false }], label: false }]
}

/**
 * The clauses an answer arrives in, in reading order: each list item whole, each paragraph by
 * sentence. Bold is dropped here — this is timing, not display — so the count matches the
 * `[data-clause]` spans the components render.
 */
export function answerClauses(blocks: readonly Block[]): string[] {
  const out: string[] = []
  for (const b of blocks) {
    if (b.kind === 'list') for (const item of b.items) out.push(item.map((r) => r.text).join(''))
    else if (b.label) out.push(b.runs.map((r) => r.text).join(''))
    else out.push(...splitClauses(b.runs.map((r) => r.text).join('')))
  }
  return out
}

/** Plain text of a block, for tests and for anything that needs the words without the shape. */
export function blockText(block: Block): string {
  return block.kind === 'list'
    ? block.items.map((i) => i.map((r) => r.text).join('')).join('\n')
    : block.runs.map((r) => r.text).join('')
}
